import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, Center, Flex, Image, Stack, Text } from "@chakra-ui/react";
import { DialogType, loginStore } from "@/modules/login/store";
import { ReactComponent as BackToTopSvg } from "@/assets/icons/mySpace/back-to-top.svg";
import { StickyContainer, Sticky } from "react-sticky";
import styles from "../style.module.scss";
import Banner from "../components/Banner";
import Nav from "../components/Nav";
import MyLike from "../components/MyLike";
import Publish from "../components/Publish";
import { HeaderWithLogin } from "@/modules/login/ui";
import { mySpaceStore } from "./store";
import { Spinner } from "@chakra-ui/react";
import { useBoolean, useMount } from "ahooks";
import Follow from "../components/Follow";
import { urlQueryParser } from "@/utils/tool";
import MyAlbum from "../components/MyAlbum";
import WaterFlow from "../components/MyAlbum/WaterFlow";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { albumStore } from "@/modules/album/store";
import { throttle } from "lodash";

export type TabKey = "MY_PUB" | "MY_ALBUM" | "MY_LIKE" | "MY_ATTENTION";
const LOCAL_TAB_KEY = "MY_SPACE_ACTIVE_TAB";
const Tabs: { keyName: TabKey; name: string }[] = [
  {
    keyName: "MY_PUB",
    name: "发布的作品",
  },
  {
    keyName: "MY_ALBUM",
    name: "我的画册",
  },
  {
    keyName: "MY_LIKE",
    name: "我的点赞",
  },
  {
    keyName: "MY_ATTENTION",
    name: "关注的人",
  },
];

const MySpace: React.FC = () => {
  const scrollDom = useRef<any>(null);
  loginStore.setGlobalLoading(false);
  const [tabVisible, setTabVisible] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab());
  const [displayMode, dmController] = useBoolean(false);
  const switchTab = (key: string) => {
    setActiveTab(key as TabKey);
    localStorage.setItem(LOCAL_TAB_KEY, key);
  };
  const localUserInfo = loginStore.userState.userInfo;
  const {
    loading: otherSpaceLoading,
    myLikeNoMore,
    myLikeList,
    setLoading,
    getMyLikeList,
    bannerUserBaseInfo,
    bannerUserStatisticsInfo,
    getBannerInfo,
  } = mySpaceStore;

  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    () => {
      const { fetchAlbums, getAlbumDetail, curAlbumId } = albumStore;
      if (displayMode) {
        getAlbumDetail(curAlbumId, true);
      } else {
        fetchAlbums(true);
      }
    },
    {
      offset: 50,
      debounce: 800,
      triggerOnNoScroll: displayMode
        ? albumStore.imagesManager.hasMore
        : albumStore.albumManager.hasMore,
    },
  );

  function defaultTab(): TabKey {
    const activeTab = localStorage.getItem(LOCAL_TAB_KEY);
    if (activeTab) {
      return activeTab as TabKey;
    }
    return "MY_LIKE";
  }
  useMount(() => {
    // 如果 url 上有 viewDetail，说明是从站内信过来的，需要使我的发布活跃
    const { viewDetail } = urlQueryParser(location.href);
    if (viewDetail) {
      setActiveTab("MY_PUB");
      return;
    }
    // 如果不是，则判断 localStorage
    const activeTab = localStorage.getItem(LOCAL_TAB_KEY);
    if (activeTab) {
      setActiveTab(activeTab as TabKey);
    }
  });

  useMount(() => {
    console.log("my space mount!");
  });

  useMount(() => {
    if (localUserInfo.userId) getBannerInfo(localUserInfo.userId);
  });
  const content = useMemo(() => {
    switch (activeTab) {
      case "MY_ATTENTION":
        return <Follow />;
      case "MY_LIKE":
        return <MyLike />;
      case "MY_PUB":
        return <Publish />;
      case "MY_ALBUM":
        return <MyAlbum onSwitch={dmController.setTrue} />;
      default:
        return null;
    }
  }, [activeTab, dmController.setTrue]);

  const handleBackTop = () => {
    scrollDom.current?.scrollTo({ top: 0, behavior: "smooth" });
    if (scrollRef.current && scrollRef.current.scrollTop) {
      scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const scrollHandle = useCallback(
    throttle((e: any) => {
      if (!scrollRef.current) return;
      const offset = scrollRef.current.scrollTop;
      if (offset <= 300) {
        setTabVisible(true);
      } else {
        setTabVisible(false);
      }
    }, 500),
    [],
  );

  useEffect(() => {
    window.addEventListener("scroll", scrollHandle, true);
    return () => window.removeEventListener("scroll", scrollHandle, true);
  });

  return (
    <div className={styles["space-scroll-wrapper"]} ref={scrollDom}>
      <HeaderWithLogin renderBell />
      <div className={styles["space-wrapper"]} id="scroll_target" ref={scrollRef}>
        {displayMode ? (
          <WaterFlow onBack={dmController.setFalse} />
        ) : (
          <>
            <Banner
              baseInfo={bannerUserBaseInfo}
              statisticsInfo={bannerUserStatisticsInfo}
              isGuest={false}
            />
            <Nav curActive={activeTab} switchTab={switchTab} tabs={Tabs} />
            {content}
          </>
        )}
      </div>
      {mySpaceStore.loading && (
        <div className={styles["loading-wrapper"]}>
          <Spinner className={styles["loading-anim"]} />
        </div>
      )}

      {!tabVisible && (
        <div className={styles["go-back-to-top"]} onClick={handleBackTop}>
          <BackToTopSvg />
        </div>
      )}
    </div>
  );
};

export default observer(MySpace);
